import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';

import { LoggingService } from '../logging/logging.service';

import { WebSocketManagerService } from './services/websocket-manager.service';
import { CacheManagerService } from './services/cache-manager.service';
import { AreaCacheStore } from './stores/area-cache-store';
import { AreaMessageHandler } from './handlers/area-message-handler';

/**
 * Registry service that initializes and connects all cache components.
 * This is the main entry point for using the cache system.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheRegistry {
  private readonly websocketManager = inject(WebSocketManagerService);
  private readonly cacheManager = inject(CacheManagerService);
  private readonly areaCacheStore = inject(AreaCacheStore);
  private readonly areaMessageHandler = inject(AreaMessageHandler);
  private readonly logger = inject(LoggingService);

  // Used instead of localStorage because cache store registration lives only in the current JS runtime.
  // Persisting the flag across reloads can incorrectly skip required registration.
  private readonly initializedProjects = new Set<string>();

  /**
   * Initialize the cache system.
   *
   * @param apiBaseUrl The base URL for the API.
   * @param projectId The ID of the project to initialize caching for.
   *
   * @returns Observable that emits true when cache registration and topic subscription are ready.
   */
  public initialize(apiBaseUrl: string, projectId: string): Observable<boolean> {
    if (this.initializedProjects.has(projectId)) {
      this.logger.debug(`Cache system already set up for project ${projectId}, skipping initialization.`);

      return of(true);
    }

    // Initialize WebSocket connection
    this.websocketManager.initialize(apiBaseUrl);

    // Register cache stores and subscribe to topics
    return this.cacheManager.registerStore({ storeName: 'areas', keyPath: 'id', topic: 'areas', projectKeyPath: 'projectId' }, this.areaCacheStore).pipe(
      switchMap(() => this.websocketManager.subscribeToTopic('areas', projectId)),
      map(() => {
        this.websocketManager.registerHandler(this.areaMessageHandler);
        this.initializedProjects.add(projectId);

        this.logger.debug(`Cache system initialized for project ${projectId}`);

        return true;
      }),
      catchError(error => {
        this.logger.error(`Error initializing cache registry: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reset runtime cache registration state.
   */
  public reset(): void {
    this.websocketManager.unsubscribeFromAllTopics();
    this.initializedProjects.clear();
  }
}
